import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Reveal, { EASE } from "@/components/Reveal";

const REVIEWS = [
    { quote: "Analytrova helped us present our business information in a clear and organized way. Their approach was professional, and the focus on our requirements made the process easy to understand.", name: "Priya Reddy", role: "Boutique Studio" },
    { quote: "Analytrova combines data-driven thinking with creative digital solutions. Their focus on professional design and practical business needs makes their services suitable for growing businesses.", name: "Sneha Reddy", role: "Entrepreneur" },
    { quote: "Analytrova provides a good combination of analytic thinking and creative solutions. Their approach focuses on understanding business goals and delivering professional digital services.", name: "Rahul Verma", role: "Small Business Owner" },
    { quote: "Analytrova brings together data, design and digital solutions in a professional way. The focus on understanding business requirements and creating practical solutions is impressive.", name: "Priya Sharma", role: "Marketing Manager" },
    { quote: "Analytrova offers a professional approach to data and digital solutions. Their focus on quality, creativity and business needs makes their services valuable.", name: "Rekha Salhan", role: "Business Owner" },
];

export default function Testimonials() {
    const [index, setIndex] = useState(0);
    const prev = () => setIndex((i) => (i - 1 + REVIEWS.length) % REVIEWS.length);
    const next = () => setIndex((i) => (i + 1) % REVIEWS.length);

    return (
        <section id="reviews" className="py-20 lg:py-28" data-testid="reviews-section">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal className="text-center max-w-2xl mx-auto">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <span className="h-px w-10 bg-gold-deep" />
                        <span className="text-[11px] font-semibold tracking-[0.34em] text-burgundy">CLIENT FEEDBACK</span>
                        <span className="h-px w-10 bg-gold-deep" />
                    </div>
                    <h2 className="text-4xl sm:text-5xl text-charcoal">What Our Clients Say</h2>
                    <p className="mt-4 text-charcoal/65">
                        We value the trust of every client and aim to deliver professional, reliable and meaningful solutions.
                    </p>
                </Reveal>

                <Reveal delay={0.1} className="mt-14 max-w-3xl mx-auto">
                    <div className="overflow-hidden" data-testid="reviews-carousel">
                        <motion.div
                            className="flex"
                            animate={{ x: `-${index * 100}%` }}
                            transition={{ duration: 0.65, ease: EASE }}
                        >
                            {REVIEWS.map((r, i) => (
                                <div key={i} className="w-full shrink-0 px-1.5 sm:px-6">
                                    <figure
                                        className="bg-cream-soft border border-burgundy/10 rounded-2xl px-8 py-12 text-center"
                                        data-testid={`review-card-${i}`}
                                    >
                                        <span className="block font-serif text-6xl text-gold leading-none select-none" aria-hidden="true">
                                            &ldquo;
                                        </span>
                                        <blockquote className="mt-3 font-serif italic text-xl sm:text-2xl text-charcoal leading-relaxed">
                                            {r.quote}
                                        </blockquote>
                                        <span className="block h-px w-12 bg-gold-deep mx-auto mt-8" aria-hidden="true" />
                                        <figcaption className="mt-6">
                                            <div className="font-medium text-charcoal">{r.name}</div>
                                            <div className="mt-1 text-[11px] tracking-[0.22em] uppercase text-olive-deep">{r.role}</div>
                                        </figcaption>
                                    </figure>
                                </div>
                            ))}
                        </motion.div>
                    </div>

                    <div className="mt-9 flex items-center justify-center gap-6">
                        <button
                            onClick={prev}
                            aria-label="Previous review"
                            className="w-11 h-11 rounded-full border border-burgundy/25 text-burgundy flex items-center justify-center transition-colors hover:bg-burgundy hover:text-cream"
                            data-testid="review-prev"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-2">
                            {REVIEWS.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setIndex(i)}
                                    aria-label={`Go to review ${i + 1}`}
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                        i === index ? "w-6 bg-burgundy" : "w-2 bg-burgundy/25 hover:bg-burgundy/50"
                                    }`}
                                    data-testid={`review-dot-${i}`}
                                />
                            ))}
                        </div>
                        <button
                            onClick={next}
                            aria-label="Next review"
                            className="w-11 h-11 rounded-full border border-burgundy/25 text-burgundy flex items-center justify-center transition-colors hover:bg-burgundy hover:text-cream"
                            data-testid="review-next"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
